import { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faChartLine, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../../context/AppContext';
import { fetchData } from '../../../services';
function formatCurrency(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '$0.00';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatShortDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatWeekRange(week) {
  return `${formatShortDate(week.week_start)} – ${formatShortDate(week.week_end)}`;
}

const TXN_TYPES = {
  invoice: 'invoice',
  bill: 'bill',
  purchase_order: 'purchase_order',
};

const TXN_TYPE_LABELS = {
  invoice: 'Invoice',
  bill: 'Bill',
  purchase_order: 'PO',
};

const TXN_TYPE_CHIPS = {
  invoice: 'status-chip--success',
  bill: 'status-chip--danger',
  purchase_order: 'status-chip--warning',
};

const QBO_TXN_URLS = {
  invoice: 'https://qbo.intuit.com/app/invoice?txnId=',
  bill: 'https://qbo.intuit.com/app/bill?txnId=',
  purchase_order: 'https://qbo.intuit.com/app/purchaseorder?txnId=',
};

function transactionType(item, fallback = TXN_TYPES.bill) {
  const type = item?.type;
  if (type === TXN_TYPES.invoice || type === TXN_TYPES.bill || type === TXN_TYPES.purchase_order) {
    return type;
  }
  return fallback;
}

function transactionPartyName(item, variant) {
  const name = variant === 'outflow' ? item?.VendorName : item?.CustomerName;
  return typeof name === 'string' ? name.trim() : '';
}

function sumWeeks(weeks, key) {
  return weeks.reduce((sum, w) => sum + (w[key] ?? 0), 0);
}

function visibleTransactions(items, includeUntracked) {
  return (items ?? []).filter((item) => includeUntracked || !item.untracked);
}

function sumTransactions(items, includeUntracked) {
  return visibleTransactions(items, includeUntracked)
    .reduce((sum, item) => sum + (Number(item.Amount) || 0), 0);
}

function partitionOutflows(items) {
  const bills = [];
  const purchaseOrders = [];
  for (const item of items ?? []) {
    if (transactionType(item, TXN_TYPES.bill) === TXN_TYPES.purchase_order) {
      purchaseOrders.push(item);
    } else {
      bills.push(item);
    }
  }
  return { bills, purchaseOrders };
}

function weekForChart(week, includeUntracked = false) {
  const totalInflow = sumTransactions(week.inflows, includeUntracked);
  const { bills, purchaseOrders } = partitionOutflows(
    visibleTransactions(week.outflows, includeUntracked),
  );
  const totalBills = sumTransactions(bills, true);
  const totalPurchaseOrders = sumTransactions(purchaseOrders, true);
  const totalOutflow = totalBills + totalPurchaseOrders;
  return {
    ...week,
    total_inflow: totalInflow,
    total_bills: totalBills,
    total_purchase_orders: totalPurchaseOrders,
    total_outflow: totalOutflow,
    net_change: totalInflow - totalOutflow,
  };
}

const CHART_PAD = { top: 20, right: 56, bottom: 36, left: 56 };
const CHART_W = 800;
const CHART_H = 260;

function formatAxisValue(val) {
  const abs = Math.abs(val);
  const sign = val < 0 ? '-' : '';
  if (abs >= 1000) return `${sign}$${Math.round(abs / 1000)}k`;
  return `${sign}$${Math.round(abs)}`;
}

function CashFlowComboChart({ weeks, beginningCashBalance, selectedWeek, onSelectWeek, onHoverWeek }) {
  const chartWeekCount = weeks.length + 1;

  const cumulative = useMemo(() => {
    const points = [{ week: 0, balance: beginningCashBalance }];
    let running = beginningCashBalance;
    weeks.forEach((w) => {
      running += w.net_change ?? 0;
      points.push({ week: w.week_number, balance: running });
    });
    return points;
  }, [weeks, beginningCashBalance]);

  const barMax = useMemo(() => {
    const peak = weeks.reduce(
      (m, w) => Math.max(m, w.total_inflow ?? 0, w.total_outflow ?? 0),
      0,
    );
    return peak > 0 ? peak * 1.12 : 1;
  }, [weeks]);

  const { lineMin, lineMax } = useMemo(() => {
    const balances = cumulative.map((p) => p.balance);
    const min = Math.min(...balances);
    const max = Math.max(...balances);
    const pad = (max - min) * 0.08 || 1;
    return { lineMin: min - pad, lineMax: max + pad };
  }, [cumulative]);

  const lineRange = lineMax - lineMin || 1;

  const plotW = CHART_W - CHART_PAD.left - CHART_PAD.right;
  const plotH = CHART_H - CHART_PAD.top - CHART_PAD.bottom;
  const groupW = plotW / Math.max(chartWeekCount, 1);
  const barW = Math.min(18, groupW * 0.32);
  const baseY = CHART_PAD.top + plotH;

  const columnCenterX = useCallback(
    (columnIndex) => CHART_PAD.left + groupW * columnIndex + groupW / 2,
    [groupW],
  );

  const balanceY = useCallback(
    (balance) => CHART_PAD.top + plotH - ((balance - lineMin) / lineRange) * plotH,
    [lineMin, lineRange, plotH],
  );

  const barTicks = useMemo(() => {
    const steps = 4;
    return Array.from({ length: steps + 1 }, (_, i) => (barMax / steps) * i);
  }, [barMax]);

  const lineTicks = useMemo(() => {
    const steps = 4;
    return Array.from({ length: steps + 1 }, (_, i) => lineMin + (lineRange / steps) * i);
  }, [lineMin, lineRange]);

  const lineCoords = cumulative.map((p, i) => ({
    ...p,
    x: columnCenterX(i),
    y: balanceY(p.balance),
  }));

  const linePath = lineCoords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x} ${c.y}`).join(' ');
  const zeroY = balanceY(0);

  return (
    <svg
      className="forecast-chart-svg"
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      role="img"
      aria-label="Weekly inflows, outflows, and cumulative net cash flow"
    >
      {barTicks.map((tick) => {
        const y = CHART_PAD.top + plotH - (tick / barMax) * plotH;
        return (
          <g key={`bar-${tick}`}>
            <line
              x1={CHART_PAD.left}
              y1={y}
              x2={CHART_W - CHART_PAD.right}
              y2={y}
              className="forecast-chart-grid"
            />
            <text x={CHART_PAD.left - 8} y={y + 4} className="forecast-chart-axis-label" textAnchor="end">
              {formatAxisValue(tick)}
            </text>
          </g>
        );
      })}

      {lineTicks.map((tick) => {
        const y = CHART_PAD.top + plotH - ((tick - lineMin) / lineRange) * plotH;
        return (
          <text
            key={`line-${tick}`}
            x={CHART_W - CHART_PAD.right + 8}
            y={y + 4}
            className="forecast-chart-axis-label forecast-chart-axis-label--right"
            textAnchor="start"
          >
            {formatAxisValue(tick)}
          </text>
        );
      })}

      <line
        x1={CHART_PAD.left}
        y1={zeroY}
        x2={CHART_W - CHART_PAD.right}
        y2={zeroY}
        className="forecast-chart-zero-line"
      />

      {weeks.map((week, i) => {
        const cx = columnCenterX(i + 1);
        const inH = ((week.total_inflow ?? 0) / barMax) * plotH;
        const billH = ((week.total_bills ?? 0) / barMax) * plotH;
        const poH = ((week.total_purchase_orders ?? 0) / barMax) * plotH;
        const isSelected = selectedWeek === week.week_number;
        const isActive = (week.total_inflow ?? 0) > 0 || (week.total_outflow ?? 0) > 0;

        return (
          <g
            key={week.week_number}
            className={`forecast-bar-group${isSelected ? ' forecast-bar-group--selected' : ''}${isActive ? ' cursor-pointer' : ' cursor-default'}`}
            onClick={() => onSelectWeek(week.week_number)}
            onMouseEnter={() => onHoverWeek(week.week_number)}
            onMouseLeave={() => onHoverWeek(null)}
          >
            <rect
              x={cx - barW - 2}
              y={baseY - inH}
              width={barW}
              height={inH}
              className="forecast-bar forecast-bar--inflow"
              rx={3}
            />
            {billH > 0 && (
              <rect
                x={cx + 2}
                y={baseY - billH}
                width={barW}
                height={billH}
                className="forecast-bar forecast-bar--bill"
                rx={3}
              />
            )}
            {poH > 0 && (
              <rect
                x={cx + 2}
                y={baseY - billH - poH}
                width={barW}
                height={poH}
                className="forecast-bar forecast-bar--purchase-order"
                rx={3}
              />
            )}
            <text
              x={cx}
              y={CHART_H - 10}
              className="forecast-chart-x-label"
              textAnchor="middle"
            >
              W{week.week_number}
            </text>
          </g>
        );
      })}

      <g
        className={`forecast-bar-group forecast-bar-group--week-zero${selectedWeek === 0 ? ' forecast-bar-group--selected' : ''} cursor-pointer`}
        onClick={() => onSelectWeek(0)}
        onMouseEnter={() => onHoverWeek(0)}
        onMouseLeave={() => onHoverWeek(null)}
      >
        <text
          x={columnCenterX(0)}
          y={CHART_H - 10}
          className="forecast-chart-x-label forecast-chart-x-label--week-zero"
          textAnchor="middle"
        >
          W0
        </text>
      </g>

      <path d={linePath} className="forecast-line-path" fill="none" />
      {lineCoords.map((c) => (
        <circle
          key={c.week}
          cx={c.x}
          cy={c.y}
          r={selectedWeek === c.week ? 5 : 4}
          className={`forecast-line-dot${selectedWeek === c.week ? ' forecast-line-dot--selected' : ''}${c.week === 0 ? ' forecast-line-dot--week-zero' : ''} cursor-pointer`}
          onClick={() => onSelectWeek(c.week)}
          onMouseEnter={() => onHoverWeek(c.week)}
          onMouseLeave={() => onHoverWeek(null)}
        />
      ))}
    </svg>
  );
}

function qboTransactionUrl(item, variant) {
  const type = transactionType(item, variant === 'outflow' ? TXN_TYPES.bill : TXN_TYPES.invoice);
  const base = QBO_TXN_URLS[type] ?? QBO_TXN_URLS.invoice;
  return `${base}${item.Id}`;
}

function TransactionRow({ item, variant }) {
  const type = transactionType(item, variant === 'outflow' ? TXN_TYPES.bill : TXN_TYPES.invoice);
  const isQboLink = Boolean(item.Id);
  const partyName = transactionPartyName(item, variant);
  const paymentDate = formatShortDate(item.PaymentDate);
  const content = (
    <>
      <div className="forecast-txn-item-main">
        <div className="forecast-txn-item-meta">
          <span className="forecast-txn-doc">{item.DocNumber}</span>
          <span className={`status-chip status-chip--xs ${TXN_TYPE_CHIPS[type]}`}>
            {TXN_TYPE_LABELS[type]}
          </span>
          {item.untracked && (
            <span className="status-chip status-chip--xs status-chip--neutral">Untracked</span>
          )}
        </div>
        {(partyName || paymentDate) && (
          <div className="forecast-txn-item-details">
            {partyName && <span className="forecast-txn-party">{partyName}</span>}
            {paymentDate && <span className="forecast-txn-date">{paymentDate}</span>}
          </div>
        )}
      </div>
      <span className={`forecast-txn-amount forecast-txn-amount--${variant}`}>
        {formatCurrency(item.Amount)}
      </span>
    </>
  );

  return (
    <li className={`forecast-txn-item${isQboLink ? ' forecast-txn-item--linkable' : ''}`}>
      {isQboLink ? (
        <a
          href={qboTransactionUrl(item, variant)}
          target="_blank"
          rel="noopener noreferrer"
          className="forecast-txn-item-link"
          title="Open in QuickBooks"
        >
          {content}
        </a>
      ) : content}
    </li>
  );
}

function TransactionGroup({ title, items, variant }) {
  const total = sumTransactions(items, true);
  return (
    <div className="forecast-txn-group">
      <h4 className="forecast-txn-group-title">
        {title}
        {items.length > 0 && (
          <span className="forecast-txn-group-amount">{formatCurrency(total)}</span>
        )}
      </h4>
      {items.length === 0 ? (
        <p className="forecast-txn-empty">No items this week</p>
      ) : (
        <ul className="forecast-txn-items">
          {items.map((item) => (
            <TransactionRow
              key={`${transactionType(item, variant === 'outflow' ? TXN_TYPES.bill : TXN_TYPES.invoice)}-${item.Id ?? item.DocNumber}`}
              item={item}
              variant={variant}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function TransactionList({ title, items, variant }) {
  const empty = !items?.length;
  const total = sumTransactions(items, true);
  const { bills, purchaseOrders } = partitionOutflows(items);

  return (
    <div className="forecast-txn-list">
      <h3 className={`forecast-txn-heading forecast-txn-heading--${variant}`}>
        {title}
        {!empty && (
          <span className="forecast-txn-heading-amount">{formatCurrency(total)}</span>
        )}
      </h3>
      {empty ? (
        <p className="forecast-txn-empty">No items this week</p>
      ) : variant === 'outflow' ? (
        <>
          <TransactionGroup title="Bills" items={bills} variant="outflow" />
          <TransactionGroup title="Purchase Orders" items={purchaseOrders} variant="outflow" />
        </>
      ) : (
        <ul className="forecast-txn-items">
          {items.map((item) => (
            <TransactionRow
              key={`${transactionType(item, TXN_TYPES.invoice)}-${item.Id ?? item.DocNumber}`}
              item={item}
              variant={variant}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function parseForecastResponse(data) {
  if (Array.isArray(data)) {
    return { weeks: data, beginningCashBalance: 0, bankAccounts: [] };
  }

  const weeks = data?.weeks ?? data?.forecast ?? [];
  const beginningCashBalance = Number(data?.beginning_cash_balance);
  const bankAccounts = Array.isArray(data?.bank_accounts) ? data.bank_accounts : [];
  return {
    weeks: Array.isArray(weeks) ? weeks : [],
    beginningCashBalance: Number.isFinite(beginningCashBalance) ? beginningCashBalance : 0,
    bankAccounts,
  };
}

async function fetchForecastData(token, baseUrl) {
  const data = await fetchData(token, `${baseUrl}/apis/admin/cash-forecast`);
  if (!data) {
    throw new Error('Failed to load forecast data');
  }
  if (data.error) {
    throw new Error(data.error);
  }
  return parseForecastResponse(data);
}

function ForecastPageHeader({ subtitle, onRefresh, refreshing, refreshDisabled }) {
  return (
    <div className="page-header">
      <div className="page-header-row">
        <div>
          <h1><FontAwesomeIcon icon={faChartLine} /> Cash Flow Forecast</h1>
          {subtitle}
        </div>
        <div className="actions">
          <button
            type="button"
            className="btn btn-secondary btn-action"
            onClick={onRefresh}
            disabled={refreshDisabled || refreshing}
            title="Refresh forecast"
          >
            <FontAwesomeIcon icon={refreshing ? faSpinner : faArrowsRotate} spin={refreshing} />
            {' '}Refresh
          </button>
        </div>
      </div>
      <hr />
    </div>
  );
}

function ChartToggle({ label, checked, onChange }) {
  return (
    <div className="archive-toggle">
      <span className="archive-toggle-label">{label}</span>
      <button
        type="button"
        className={`toggle-switch${checked ? ' toggle-switch--on' : ''}`}
        onClick={() => onChange(!checked)}
        role="switch"
        aria-checked={checked}
        aria-label={label}
      >
        <span className="toggle-switch-thumb" />
      </button>
    </div>
  );
}

export const CashFlowForecastPage = () => {
  const { token, base_url } = useAppContext();
  const [weeks, setWeeks] = useState([]);
  const [beginningCashBalance, setBeginningCashBalance] = useState(0);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [hoveredWeek, setHoveredWeek] = useState(null);
  const [includeUntrackedInChart, setIncludeUntrackedInChart] = useState(false);

  const loadForecast = useCallback(async ({ isRefresh = false, shouldApply = () => true } = {}) => {
    if (!token) return;

    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const {
        weeks: forecastWeeks,
        beginningCashBalance: startingBalance,
        bankAccounts: accounts,
      } = await fetchForecastData(token, base_url);
      if (!shouldApply()) return;

      setWeeks(forecastWeeks);
      setBeginningCashBalance(startingBalance);
      setBankAccounts(accounts);
      if (!isRefresh) {
        setSelectedWeek(0);
      }
    } catch (err) {
      if (!shouldApply()) return;
      setError(err?.message || 'Failed to load forecast data');
    } finally {
      if (!shouldApply()) return;
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, [token, base_url]);

  useEffect(() => {
    let cancelled = false;
    loadForecast({ shouldApply: () => !cancelled });
    return () => {
      cancelled = true;
    };
  }, [loadForecast]);

  const handleRefresh = useCallback(() => {
    loadForecast({ isRefresh: true });
  }, [loadForecast]);

  const chartWeeks = useMemo(
    () => weeks.map((week) => weekForChart(week, includeUntrackedInChart)),
    [weeks, includeUntrackedInChart],
  );

  const totals = useMemo(() => ({
    inflow: sumWeeks(chartWeeks, 'total_inflow'),
    outflow: sumWeeks(chartWeeks, 'total_outflow'),
    net: sumWeeks(chartWeeks, 'net_change'),
  }), [chartWeeks]);

  const activeWeekCount = useMemo(
    () => chartWeeks.filter((w) => (w.total_inflow ?? 0) > 0 || (w.total_outflow ?? 0) > 0).length,
    [chartWeeks],
  );

  const selectedWeekData = useMemo(
    () => weeks.find((w) => w.week_number === selectedWeek) ?? null,
    [weeks, selectedWeek],
  );

  const selectedWeekTotals = useMemo(
    () => (selectedWeekData ? weekForChart(selectedWeekData, true) : null),
    [selectedWeekData],
  );

  const tooltipWeek = useMemo(
    () => chartWeeks.find((w) => w.week_number === hoveredWeek) ?? null,
    [chartWeeks, hoveredWeek],
  );

  const cumulativeByWeek = useMemo(() => {
    const map = new Map([[0, beginningCashBalance]]);
    let running = beginningCashBalance;
    chartWeeks.forEach((w) => {
      running += w.net_change ?? 0;
      map.set(w.week_number, running);
    });
    return map;
  }, [chartWeeks, beginningCashBalance]);

  const dateRange = useMemo(() => {
    if (!weeks.length) return '';
    return `${formatShortDate(weeks[0].week_start)} – ${formatShortDate(weeks[weeks.length - 1].week_end)}`;
  }, [weeks]);

  const handleSelectWeek = useCallback((weekNum) => {
    setSelectedWeek(weekNum);
  }, []);

  if (loading) {
    return (
      <div className="page">
        <div className="main-content">
          <ForecastPageHeader
            onRefresh={handleRefresh}
            refreshing={refreshing}
            refreshDisabled
          />
          <p className="loading-panel forecast-loading">Loading forecast…</p>
        </div>
      </div>
    );
  }

  if (error && weeks.length === 0) {
    return (
      <div className="page">
        <div className="main-content">
          <ForecastPageHeader
            onRefresh={handleRefresh}
            refreshing={refreshing}
          />
          <p className="forecast-error">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="main-content">
        <ForecastPageHeader
          onRefresh={handleRefresh}
          refreshing={refreshing}
          subtitle={(
            <p className="forecast-subtitle">
              Projected cash movements across {weeks.length} weeks
              {dateRange ? ` · ${dateRange}` : ''}
            </p>
          )}
        />

        {error && <p className="forecast-error">{error}</p>}

        <div className="forecast-stats-grid">
          <div className="forecast-stat-card forecast-stat-card--success">
            <span className="forecast-stat-label">Total Inflows</span>
            <span className="forecast-stat-value">{formatCurrency(totals.inflow)}</span>
          </div>
          <div className="forecast-stat-card forecast-stat-card--error">
            <span className="forecast-stat-label">Total Outflows</span>
            <span className="forecast-stat-value">{formatCurrency(totals.outflow)}</span>
          </div>
          <div className={`forecast-stat-card${totals.net >= 0 ? ' forecast-stat-card--success' : ' forecast-stat-card--error'}`}>
            <span className="forecast-stat-label">Net Change</span>
            <span className="forecast-stat-value">{formatCurrency(totals.net)}</span>
          </div>
          <div className="forecast-stat-card">
            <span className="forecast-stat-label">Active Weeks</span>
            <span className="forecast-stat-value">{activeWeekCount}</span>
          </div>
        </div>

        <div className="forecast-chart-card">
          <div className="forecast-chart-header">
            <div>
              <h2 className="forecast-chart-title">Cash Flow Overview</h2>
              <p className="forecast-chart-subtitle">
                Weekly inflows &amp; outflows with cumulative net cash flow — click a week for details
              </p>
            </div>
            <div className="forecast-chart-header-actions">
              <ChartToggle
                label="Include untracked"
                checked={includeUntrackedInChart}
                onChange={setIncludeUntrackedInChart}
              />
              <div className="forecast-chart-legend">
              <span className="forecast-legend-item">
                <span className="forecast-legend-swatch forecast-legend-swatch--inflow" />
                Inflows
              </span>
              <span className="forecast-legend-item">
                <span className="forecast-legend-swatch forecast-legend-swatch--bill" />
                Bills
              </span>
              <span className="forecast-legend-item">
                <span className="forecast-legend-swatch forecast-legend-swatch--purchase-order" />
                Purchase Orders
              </span>
              <span className="forecast-legend-item">
                <span className="forecast-legend-swatch forecast-legend-swatch--net" />
                Cumulative Net
              </span>
              </div>
            </div>
          </div>
          <div className="forecast-chart-body">
            <CashFlowComboChart
              weeks={chartWeeks}
              beginningCashBalance={beginningCashBalance}
              selectedWeek={selectedWeek}
              onSelectWeek={handleSelectWeek}
              onHoverWeek={setHoveredWeek}
            />
            {hoveredWeek != null && (
              <div className="forecast-chart-tooltip">
                <strong>Week {hoveredWeek}</strong>
                {hoveredWeek === 0 ? (
                  <>
                    <span>Current cash position</span>
                    <span className="forecast-tooltip-cumulative">
                      Balance: {formatCurrency(beginningCashBalance)}
                    </span>
                  </>
                ) : tooltipWeek && (
                  <>
                    <span>{formatWeekRange(tooltipWeek)}</span>
                    <span className="forecast-tooltip-inflow">
                      In: {formatCurrency(tooltipWeek.total_inflow)}
                    </span>
                    <span className="forecast-tooltip-outflow">
                      Out: {formatCurrency(tooltipWeek.total_outflow)}
                    </span>
                    <span className="forecast-tooltip-outflow-split">
                      Bills {formatCurrency(tooltipWeek.total_bills)}
                      {' · '}
                      POs {formatCurrency(tooltipWeek.total_purchase_orders)}
                    </span>
                    <span className={tooltipWeek.net_change >= 0 ? 'forecast-tooltip-positive' : 'forecast-tooltip-negative'}>
                      Net: {formatCurrency(tooltipWeek.net_change)}
                    </span>
                    <span className="forecast-tooltip-cumulative">
                      Balance: {formatCurrency(cumulativeByWeek.get(tooltipWeek.week_number) ?? 0)}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="forecast-chart-axis-keys">
            <span className="forecast-axis-key forecast-axis-key--left">Weekly amounts</span>
            <span className="forecast-axis-key forecast-axis-key--right">Cumulative net</span>
          </div>
        </div>

        <div className="forecast-week-nav">
          <button
            type="button"
            className={`forecast-week-btn${selectedWeek === 0 ? ' active' : ''}`}
            onClick={() => handleSelectWeek(0)}
          >
            Week 0
          </button>
          {chartWeeks.map((w) => {
            const hasActivity = (w.total_inflow ?? 0) > 0 || (w.total_outflow ?? 0) > 0;
            return (
              <button
                key={w.week_number}
                type="button"
                className={`forecast-week-btn${selectedWeek === w.week_number ? ' active' : ''}${!hasActivity ? ' forecast-week-btn--quiet' : ''}`}
                onClick={() => handleSelectWeek(w.week_number)}
              >
                Week {w.week_number}
              </button>
            );
          })}
        </div>

        {selectedWeek === 0 && (
          <div className="forecast-detail-card">
            <div className="forecast-detail-header">
              <h2 className="forecast-detail-title">Week 0</h2>
              <span className="forecast-detail-range">Current cash position</span>
              <span className={`forecast-detail-net${beginningCashBalance >= 0 ? ' forecast-detail-net--positive' : ' forecast-detail-net--negative'}`}>
                {formatCurrency(beginningCashBalance)}
              </span>
            </div>
            <p className="forecast-week-zero-note">
              Starting balance before forecasted inflows and outflows begin in Week 1.
            </p>
            {bankAccounts.length > 0 && (
              <div className="forecast-txn-list forecast-bank-accounts">
                <h3 className="forecast-txn-heading">Bank Accounts</h3>
                <ul className="forecast-txn-items">
                  {bankAccounts.map((account) => (
                    <li key={account.id ?? account.name} className="forecast-txn-item">
                      <span className="forecast-txn-doc">{account.name}</span>
                      <span className="forecast-txn-amount">
                        {formatCurrency(account.balance)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {selectedWeek !== 0 && selectedWeekData && (
          <div className="forecast-detail-card">
            <div className="forecast-detail-header">
              <h2 className="forecast-detail-title">
                Week {selectedWeekData.week_number}
              </h2>
              <span className="forecast-detail-range">{formatWeekRange(selectedWeekData)}</span>
              <span className={`forecast-detail-net${(selectedWeekTotals?.net_change ?? 0) >= 0 ? ' forecast-detail-net--positive' : ' forecast-detail-net--negative'}`}>
                Net {formatCurrency(selectedWeekTotals?.net_change)}
              </span>
            </div>
            <div className="forecast-detail-grid">
              <TransactionList
                title="Inflows"
                items={selectedWeekData.inflows}
                variant="inflow"
              />
              <TransactionList
                title="Outflows"
                items={selectedWeekData.outflows}
                variant="outflow"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
